import { useEffect, useState, useRef } from 'react';
import { Map } from 'mapbox-gl';
import { BaseNode } from './useNearbyBaseNodes';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store/store';
import {
  getHeatmapDataForHeight,
  getHeatmapUrls,
  extractBoundsFromKML,
} from '../utils/mapUtils';

interface UseHeatmapOverlayOptions {
  map: Map | null;
  baseNode: BaseNode | null;
}

interface UseHeatmapOverlayReturn {
  isLoading: boolean;
  error: string | null;
  legendUrl: string | null;
}

/**
 * Hook to manage heatmap overlay on the map
 * Fetches KML bounds, loads PNG overlay, and displays as raster layer
 */
export const useHeatmapOverlay = ({
  map,
  baseNode,
}: UseHeatmapOverlayOptions): UseHeatmapOverlayReturn => {
  const { activeHeatmap, activeHeight } = useAppSelector(
    (state: RootState) => state.servicePredictor.filters
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [legendUrl, setLegendUrl] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const eventHandlersRef = useRef<{
    data?: (e: any) => void;
    error?: (e: any) => void;
    timeoutId?: NodeJS.Timeout;
  } | null>(null);

  useEffect(() => {
    const cleanup = () => {
      if (!map) return;

      if (eventHandlersRef.current) {
        if (eventHandlersRef.current.data) {
          map.off('data', eventHandlersRef.current.data);
        }
        if (eventHandlersRef.current.error) {
          map.off('error', eventHandlersRef.current.error);
        }
        if (eventHandlersRef.current.timeoutId) {
          clearTimeout(eventHandlersRef.current.timeoutId);
        }
        eventHandlersRef.current = null;
      }

      if (map.getLayer('heatmap-overlay')) {
        map.removeLayer('heatmap-overlay');
      }
      if (map.getSource('heatmap-overlay')) {
        map.removeSource('heatmap-overlay');
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      setIsLoading(false);
      setError(null);
      setLegendUrl(null);
    };

    if (!map || !activeHeatmap || !activeHeight || !baseNode) {
      cleanup();
      return;
    }

    if (!baseNode.heatmap_list || baseNode.heatmap_list.length === 0) {
      cleanup();
      return;
    }

    const heatmapItem = getHeatmapDataForHeight(
      baseNode.heatmap_list,
      activeHeight
    );
    if (!heatmapItem) {
      cleanup();
      setError(`No heatmap data found for height: ${activeHeight}`);
      return;
    }

    const urls = getHeatmapUrls(heatmapItem, activeHeatmap);
    if (!urls) {
      cleanup();
      setError(`No heatmap URLs found for type: ${activeHeatmap}`);
      return;
    }

    setLegendUrl(
      urls.legendUrl && urls.legendUrl.trim() !== '' ? urls.legendUrl : null
    );

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);
    setError(null);

    const fetchKMLAndAddOverlay = async () => {
      try {
        const response = await fetch(urls.kmlUrl, { signal });
        if (!response.ok) {
          const statusText = response.statusText || `HTTP ${response.status}`;
          const errorMessage =
            response.status === 404
              ? `Heatmap KML file not found (404). The heatmap data may not be available for this configuration.`
              : `Failed to fetch KML: ${statusText} (${response.status})`;
          throw new Error(errorMessage);
        }

        const kmlText = await response.text();
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(kmlText, 'application/xml');

        const parseError = kmlDoc.querySelector('parsererror');
        if (parseError) {
          throw new Error('Failed to parse KML file');
        }

        const bounds = extractBoundsFromKML(kmlDoc);
        if (!bounds) {
          throw new Error('No bounds found in KML file');
        }

        if (map.getLayer('heatmap-overlay')) {
          map.removeLayer('heatmap-overlay');
        }
        if (map.getSource('heatmap-overlay')) {
          map.removeSource('heatmap-overlay');
        }

        // coordinates array for image source
        // [top-left, top-right, bottom-right, bottom-left]
        // Mapbox expects [west, north], [east, north], [east, south], [west, south]
        const coordinates: [
          [number, number],
          [number, number],
          [number, number],
          [number, number],
        ] = [
          [bounds.west, bounds.north],
          [bounds.east, bounds.north],
          [bounds.east, bounds.south],
          [bounds.west, bounds.south],
        ];

        map.addSource('heatmap-overlay', {
          type: 'image',
          url: urls.overlayUrl,
          coordinates: coordinates,
        });

        const errorHandler = (e: any) => {
          if (e.sourceId === 'heatmap-overlay' && !signal.aborted) {
            setIsLoading(false);
            setError(
              'Failed to load heatmap image. The image may be unavailable or inaccessible.'
            );
            console.error('Heatmap image source error:', e);
            // Clean up event handlers
            if (eventHandlersRef.current) {
              if (eventHandlersRef.current.data) {
                map.off('data', eventHandlersRef.current.data);
              }
              map.off('error', errorHandler);
              eventHandlersRef.current = null;
            }
          }
        };
        map.on('error', errorHandler);

        const beforeId = map.getLayer('sites-clusters')
          ? 'sites-clusters'
          : undefined;
        map.addLayer(
          {
            id: 'heatmap-overlay',
            type: 'raster',
            source: 'heatmap-overlay',
            paint: {
              'raster-opacity': 0.8,
              'raster-fade-duration': 500,
            },
          },
          beforeId
        );

        const handleData = (e: any) => {
          if (signal.aborted || !map || e.sourceId !== 'heatmap-overlay')
            return;

          const source = map.getSource('heatmap-overlay');
          if (source && source.type === 'image') {
            if (e.isSourceLoaded) {
              setIsLoading(false);
              if (eventHandlersRef.current) {
                if (eventHandlersRef.current.data) {
                  map.off('data', eventHandlersRef.current.data);
                }
                if (eventHandlersRef.current.error) {
                  map.off('error', eventHandlersRef.current.error);
                }
                if (eventHandlersRef.current.timeoutId) {
                  clearTimeout(eventHandlersRef.current.timeoutId);
                }
                eventHandlersRef.current = null;
              }
            }
          }
        };

        map.on('data', handleData);

        // Fallback timeout: if image loads very quickly (cached), set loading to false
        // This ensures loading state doesn't hang if the data event fires before we set up the listener
        const timeoutId = setTimeout(() => {
          if (!signal.aborted && map) {
            const source = map.getSource('heatmap-overlay');
            if (source && source.type === 'image') {
              setIsLoading(false);
              // Clean up event handlers
              if (eventHandlersRef.current) {
                if (eventHandlersRef.current.data) {
                  map.off('data', eventHandlersRef.current.data);
                }
                if (eventHandlersRef.current.error) {
                  map.off('error', eventHandlersRef.current.error);
                }
                if (eventHandlersRef.current.timeoutId) {
                  clearTimeout(eventHandlersRef.current.timeoutId);
                }
                eventHandlersRef.current = null;
              }
            }
          }
        }, 500);

        eventHandlersRef.current = {
          data: handleData,
          error: errorHandler,
          timeoutId: timeoutId,
        };
      } catch (err: any) {
        if (signal.aborted) return;
        setIsLoading(false);
        setError(err.message || 'Failed to load heatmap');
        console.error('Error loading heatmap:', err);
      }
    };

    fetchKMLAndAddOverlay();

    // Cleanup on unmount or when dependencies change
    return cleanup;
  }, [map, baseNode, activeHeatmap, activeHeight]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { isLoading, error, legendUrl };
};
